/**
 * First-party usage telemetry (spec 14 / ATL-70).
 *
 * Both clients report the *route pattern* they are on (web: TanStack matched
 * route `fullPath` like `/peaks/$peakId`; mobile: expo-router segments like
 * `(tabs)/peak/[id]`), never the concrete URL, so no ids or slugs leave the
 * device. `normaliseScreen` maps either pattern onto one screen vocabulary so
 * the admin can compare web and mobile like for like.
 */

export type TelemetryClient = "web" | "ios" | "android";

export const CLIENT_HEADER = "X-Atlas-Client";
export const APP_VERSION_HEADER = "X-Atlas-App-Version";
export const BACKGROUND_HEADER = "X-Atlas-Background";

export interface UsageEventInput {
  kind: "screen" | "action";
  /** Normalised screen name (see SCREEN_ALIASES / normaliseScreen). */
  name: string;
  /** Route pattern as the client saw it, for vocabulary debugging. */
  path?: string;
  meta?: Record<string, unknown>;
  /** ISO timestamp. */
  occurredAt: string;
}

/**
 * Canonical screen names. Keys are cleaned route patterns (see cleanPattern):
 * route groups removed, params as `:param`, leading slash, no trailing slash.
 * Anything not listed falls back to the cleaned pattern itself, so a new
 * screen still shows up in the admin (under its raw pattern) until it is
 * given a name here.
 */
export const SCREEN_ALIASES: Record<string, string> = {
  // Home / core
  "/": "dashboard",
  "/dashboard": "dashboard",
  "/index": "dashboard",
  "/map": "map",
  "/explore": "map",
  "/statistics": "statistics",
  "/profile/statistics": "statistics",
  "/activities": "activities",
  "/activities/:id/review": "activity_review",
  "/profile/activities": "activities",
  "/profile/activities/:id": "activity_detail",
  "/activity/:id": "activity_detail", // mobile root alias
  "/trips/:tripId": "trip_detail", // web (spec 35)
  "/trip/:id": "trip_detail", // mobile root screen (spec 35)
  "/share/trip/:id": "trip_share", // web public share page
  // Collections and items
  "/collections": "collections",
  "/collections/index": "collections",
  "/collections/:slug": "collection_detail",
  "/collection/:slug": "collection_detail", // mobile root alias
  "/peaks/:slug": "peak_detail",
  "/peak/:slug": "peak_detail",
  "/trails/:id": "trail_detail",
  "/trail/:id": "trail_detail",
  "/landmarks/:slug": "landmark_detail",
  "/landmark/:slug": "landmark_detail",
  "/regions": "regions",
  "/regions/:slug": "region_detail",
  "/collections/regions": "regions",
  "/collections/regions/index": "regions",
  "/collections/regions/:slug": "region_detail",
  "/region/:slug": "region_detail", // mobile root alias
  "/lists": "lists",
  "/lists/:id": "list_detail",
  "/profile/lists": "lists",
  "/profile/lists/index": "lists",
  "/profile/lists/:id": "list_detail",
  "/explorers/:username/lists/:id": "list_detail",
  "/plan": "goals",
  // Progress
  "/achievements": "achievements",
  "/profile/achievements": "achievements",
  "/wrapped": "wrapped",
  "/wrapped/:year": "wrapped",
  "/profile/wrapped": "wrapped",
  // Feedback threads (web /feedback, mobile Profile > Your feedback)
  "/feedback": "feedback_list",
  "/feedback/:id": "feedback_thread",
  "/profile/your-feedback": "feedback_list",
  "/profile/your-feedback/index": "feedback_list",
  "/profile/your-feedback/:id": "feedback_thread",
  // Social
  "/feed": "feed",
  // Mobile Community tab (Groups | Feed on one route). The route itself is
  // "community"; the screen also reports its segment as a pseudo pattern
  // (/community/groups, /community/feed) so feed and groups keep their names.
  "/community": "community",
  "/community/index": "community",
  "/community/feed": "feed",
  "/community/groups": "groups",
  "/community/post/:id": "post_detail",
  "/post/:id": "post_detail", // mobile root alias
  "/explorers/:username": "explorer_profile",
  // Groups (spec 32 / ATL-151), mobile patterns.
  "/community/groups/join": "group_join",
  "/community/groups/:id": "group_detail",
  "/community/groups/:id/index": "group_detail",
  "/community/groups/:id/settings": "group_settings",
  "/community/groups/:id/challenges/new": "group_challenge_new",
  "/community/groups/:id/challenges/:cid": "group_challenge",
  "/community/groups/:id/collections/:slug": "group_collection_board",
  "/groups/join/:code": "group_join",
  // Groups, web patterns (TanStack params as in router.tsx).
  "/groups": "groups",
  "/groups/:id": "group_detail",
  "/groups/:id/settings": "group_settings",
  "/groups/:id/challenges/:cid": "group_challenge",
  "/groups/:id/collections/:slug": "group_collection_board",
  "/showcase": "showcase",
  // Profile / account
  "/profile": "profile",
  "/profile/index": "profile",
  "/profile/customisation": "customisation",
  "/profile/notifications": "notification_settings",
  "/profile/integrations": "integrations",
  "/connected-sources": "integrations",
  "/profile/store": "store",
  "/store": "store",
  "/profile/public-profile": "public_profile_settings",
  "/profile/change-password": "settings",
  "/profile/danger-zone": "settings",
  "/profile/feedback": "feedback",
  "/profile/credits": "credits",
  "/credits": "credits",
  "/profile/offline-maps": "offline_maps",
  "/checkin-nearby": "checkin_nearby",
  "/upgrade": "upgrade",
  // Tools
  "/route-analyser": "route_analyser",
  "/routes/:id": "route_share",
  "/where-to-start": "where_to_start",
  // Public / auth
  "/login": "login",
  "/signup": "signup",
  // Mobile first run (spec 29 / ATL-131). /welcome is pre-login so it is only
  // ever reported by clients that enable telemetry while signed out (none
  // today); /setup is the post-signup stepper.
  "/welcome": "welcome",
  "/setup": "setup",
  // Web: one-off "add your email" step after a Strava-first signup.
  "/welcome/email": "welcome_email",
  "/beta/:code": "partner_landing",
  "/with/:code": "partner_landing",
  "/join/:username": "join",
  "/profile/invites": "invites",
  "/admin/referrals": "admin_referrals",
  "/share/activity/:id": "activity_share",
  "/privacy": "legal",
  "/terms": "legal",
  "/community-guidelines": "legal",
};

/**
 * Turn a web or mobile route pattern into the shared key form:
 *  - strips expo-router groups `(tabs)` and web pathless layouts `_protected`
 *  - `$param` (TanStack) and `[param]` / `[...param]` (expo) become `:param`
 *  - collapses duplicate slashes, drops trailing slash, lowercases
 */
export function cleanPattern(pattern: string): string {
  const parts = pattern
    .split("/")
    .filter((seg) => seg.length > 0)
    .filter((seg) => !(seg.startsWith("(") && seg.endsWith(")")))
    .filter((seg) => !seg.startsWith("_"))
    .map((seg) => {
      if (seg.startsWith("$")) return `:${seg.slice(1)}`;
      const m = /^\[(?:\.\.\.)?(.+)\]$/.exec(seg);
      if (m) return `:${m[1]}`;
      return seg.toLowerCase();
    });
  return "/" + parts.join("/");
}

/**
 * Canonical screen name for a route pattern. Unknown patterns return the
 * cleaned pattern (prefixed with the client so web/mobile leftovers stay
 * distinguishable in the admin) rather than "unknown", so nothing is lost.
 */
export function normaliseScreen(client: TelemetryClient, pattern: string): string {
  const cleaned = cleanPattern(pattern);
  const alias = SCREEN_ALIASES[cleaned];
  if (alias) return alias;
  // Nested web routes: /admin/users -> admin, /collections/x/y -> try prefixes.
  if (cleaned.startsWith("/admin")) return "admin";
  const segs = cleaned.split("/").filter(Boolean);
  for (let n = segs.length - 1; n >= 1; n--) {
    const prefix = "/" + segs.slice(0, n).join("/");
    const a = SCREEN_ALIASES[prefix];
    if (a && n === segs.length - 1 && !segs[n]!.startsWith(":")) return `${a}_${segs[n]}`;
  }
  return `${client === "web" ? "web" : "app"}:${cleaned}`;
}

export interface TelemetryOptions {
  client: TelemetryClient;
  /** POST the batch. Should throw on failure so events are re-queued. */
  send: (events: UsageEventInput[]) => Promise<void>;
  /** Flush when this many events are queued (default 20). */
  flushAt?: number;
  /** Flush at least this often while events are queued (default 30s). */
  flushIntervalMs?: number;
  /** Optional predicate: return false to drop events (e.g. logged out). */
  isEnabled?: () => boolean;
  now?: () => Date;
}

export interface Telemetry {
  /** Record a screen view for a route pattern; dedupes consecutive repeats. */
  screen: (pattern: string, meta?: Record<string, unknown>) => void;
  /** Record an arbitrary event with an explicit name. */
  track: (kind: "screen" | "action", name: string, extra?: Partial<UsageEventInput>) => void;
  /** Send whatever is queued now (app going to background, pagehide, logout). */
  flush: () => Promise<void>;
  /** Drop the queue without sending (logout). */
  reset: () => void;
  /** Number of queued events (tests / debugging). */
  size: () => number;
}

/**
 * Batching helper shared by web and mobile. Flushes at `flushAt` events, on a
 * timer, or when the host calls flush() from a lifecycle hook. Failed sends
 * are re-queued (capped at 200 events) so a flaky connection loses nothing.
 */
export function createTelemetry(opts: TelemetryOptions): Telemetry {
  const flushAt = opts.flushAt ?? 20;
  const flushIntervalMs = opts.flushIntervalMs ?? 30_000;
  const now = opts.now ?? (() => new Date());
  const MAX_QUEUE = 200;

  let queue: UsageEventInput[] = [];
  let timer: ReturnType<typeof setTimeout> | null = null;
  let inflight: Promise<void> | null = null;
  let lastScreen: string | null = null;

  const clearTimer = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const flush = async (): Promise<void> => {
    clearTimer();
    if (inflight) {
      await inflight;
    }
    if (queue.length === 0) return;
    const batch = queue;
    queue = [];
    inflight = opts
      .send(batch)
      .catch(() => {
        // Re-queue in front, capped so an offline day doesn't grow unbounded.
        queue = [...batch, ...queue].slice(-MAX_QUEUE);
      })
      .finally(() => {
        inflight = null;
      });
    await inflight;
  };

  const push = (event: UsageEventInput) => {
    if (opts.isEnabled && !opts.isEnabled()) return;
    queue.push(event);
    if (queue.length > MAX_QUEUE) queue = queue.slice(-MAX_QUEUE);
    if (queue.length >= flushAt) {
      void flush();
    } else if (!timer) {
      timer = setTimeout(() => void flush(), flushIntervalMs);
    }
  };

  return {
    screen(pattern, meta) {
      const name = normaliseScreen(opts.client, pattern);
      // The same screen re-rendering (theme remount, param change on the
      // same route) is not a new view.
      if (name === lastScreen) return;
      lastScreen = name;
      push({
        kind: "screen",
        name,
        path: cleanPattern(pattern),
        ...(meta ? { meta } : {}),
        occurredAt: now().toISOString(),
      });
    },
    track(kind, name, extra) {
      push({ kind, name, ...extra, occurredAt: extra?.occurredAt ?? now().toISOString() });
    },
    flush,
    reset() {
      clearTimer();
      queue = [];
      lastScreen = null;
    },
    size: () => queue.length,
  };
}
